import {
  WorkflowCaseItemDTO,
  WorkflowSummaryDTO,
  WorkflowSummaryResponseDTO,
  WorkflowUserDTO,
} from '@peritasai/types';

/**
 * Individual case item entity for workflow summary cards
 */
export class WorkflowCaseItemEntity {
  public readonly id: string;
  public readonly caseHospitalId?: string;
  public readonly surgeryTime: string;
  public readonly dueTime: string;
  public readonly action?: 'Dispatch' | 'Build' | 'View';
  public readonly type?:
    | 'Ready for Dispatch'
    | 'Build Incomplete'
    | 'Build Not Started';
  public readonly trays?: number;
  public readonly isPriority?: boolean;

  constructor(dto: WorkflowCaseItemDTO) {
    this.id = dto.id;
    this.caseHospitalId = dto.caseHospitalId;
    this.surgeryTime = dto.surgeryTime;
    this.dueTime = dto.dueTime;
    this.action = dto.action;
    this.type = dto.type;
    this.trays = dto.trays;
    this.isPriority = dto.isPriority;
  }

  /**
   * Convert back to DTO for API communication
   */
  public toDTO(): WorkflowCaseItemDTO {
    return {
      id: this.id,
      caseHospitalId: this.caseHospitalId,
      surgeryTime: this.surgeryTime,
      dueTime: this.dueTime,
      action: this.action,
      type: this.type,
      trays: this.trays,
      isPriority: this.isPriority,
    };
  }
}

/**
 * User information entity for in-progress cases
 */
export class WorkflowUserEntity {
  public readonly initials: string;
  public readonly name: string;

  constructor(dto: WorkflowUserDTO) {
    this.initials = dto.initials;
    this.name = dto.name;
  }

  /**
   * Convert back to DTO for API communication
   */
  public toDTO(): WorkflowUserDTO {
    return {
      initials: this.initials,
      name: this.name,
    };
  }
}

/**
 * In-progress cases information entity
 */
export interface InProgressCasesEntity {
  total: number;
  user?: WorkflowUserEntity;
}

/**
 * Dispatched cases information entity
 */
export interface DispatchedCasesEntity {
  total: number;
  complete: number;
  incomplete: number;
}

/**
 * Workflow summary entity for a specific date
 */
export class WorkflowSummaryEntity {
  public readonly date: string;
  public readonly totalCasesDue: number;
  public readonly completionPercentage: number;
  public readonly actionableCases: WorkflowCaseItemEntity[];
  public readonly blockedCases: WorkflowCaseItemEntity[];
  public readonly inProgressCases: InProgressCasesEntity;
  public readonly dispatchedCases: DispatchedCasesEntity;

  constructor(dto: WorkflowSummaryDTO) {
    this.date = dto.date;
    this.totalCasesDue = dto.totalCasesDue;
    this.completionPercentage = dto.completionPercentage;
    this.actionableCases = dto.actionableCases.map(
      (item) => new WorkflowCaseItemEntity(item)
    );
    this.blockedCases = dto.blockedCases.map(
      (item) => new WorkflowCaseItemEntity(item)
    );
    this.inProgressCases = {
      total: dto.inProgressCases.total,
      user: dto.inProgressCases.user
        ? new WorkflowUserEntity(dto.inProgressCases.user)
        : undefined,
    };
    this.dispatchedCases = dto.dispatchedCases;
  }

  /**
   * Convert back to DTO for API communication
   */
  public toDTO(): WorkflowSummaryDTO {
    return {
      date: this.date,
      totalCasesDue: this.totalCasesDue,
      completionPercentage: this.completionPercentage,
      actionableCases: this.actionableCases.map((item) => item.toDTO()),
      blockedCases: this.blockedCases.map((item) => item.toDTO()),
      inProgressCases: {
        total: this.inProgressCases.total,
        user: this.inProgressCases.user?.toDTO(),
      },
      dispatchedCases: this.dispatchedCases,
    };
  }
}

/**
 * Response entity from the workflow summary API
 */
export class WorkflowSummaryResponseEntity {
  public readonly summary: WorkflowSummaryEntity;
  public readonly dateOptions: Array<{
    date: string;
    label: string;
    completionPercentage: number;
  }>;

  constructor(dto: WorkflowSummaryResponseDTO) {
    this.summary = new WorkflowSummaryEntity(dto.summary);
    this.dateOptions = dto.dateOptions;
  }

  /**
   * Convert back to DTO for API communication
   */
  public toDTO(): WorkflowSummaryResponseDTO {
    return {
      summary: this.summary.toDTO(),
      dateOptions: this.dateOptions,
    };
  }
}
